import Cookies from 'js-cookie'
import Util from 'util/util'

/**
 * Opens the WoodWing asset picker of the GCM team (Sanoma Learning), the web component
 * `<sl-gcm-asset-picker-modal>` from `@sl-generic-content-module/sl-asset-picker`.
 *
 * The picker runs in an iframe (entry point AssetPickerFrame.js in this folder), because its bundle
 * replaces global browser objects such as customElements and HTMLElement. In the iframe that
 * stays out of Learnbeat's window. The iframe is on our own domain, so the picker's API calls
 * to `proxyPath` still carry the user's session. The backend adds the GCM access token there
 * and forwards them to the GCM gateway.
 *
 * Note that the iframe isolates the picker from our code, not the other way around: a page
 * on our own domain can still reach Learnbeat's window. It is no security boundary.
 */
export default class AssetPicker {

    static proxyPath = '/woodwing_assets'

    static settingsURL = '/woodwing_assets/settings.json'

    // The same Typekit kit that default.blade.php in the backend loads for Learnbeat itself.
    static fontStylesheetURL = 'https://use.typekit.net/zar3sch.css'

    // The separate 'asset-picker-frame' entry point, from the same place as the rest of
    // Learnbeat's bundles.
    static frameStylesheetURL = `${window.resourceLocation}/css/asset-picker-frame.css`
    static frameScriptURL = `${window.resourceLocation}/js/asset-picker-frame.${Util.getShortLanguage}.js`

    // Promise for the iframe and the settings, so both are only loaded once.
    static loadingFrameAndSettings = null

    static isOpenOrLoading = false

    /**
     * Opens the picker and waits until the user inserts an asset or closes the picker.
     *
     * A regular upload has its file type checked by the file dialog and by the upload server,
     * but an asset passes through neither. So an asset that doesn't match `accept` is refused
     * here: the user sees `wrongFileTypeMessage` and the caller gets null.
     *
     * @param {Object} options
     * @param {String} options.accept                 Optional. Accept string of the upload, like the
     *                                                file dialog's. Without it, every file type is accepted.
     * @param {String} options.wrongFileTypeMessage   Message for an asset that doesn't match `accept`.
     * @return {Promise<Object|null>}   the Asset the user inserted, or null when no fitting asset was inserted
     */
    static async open({ accept, wrongFileTypeMessage } = {}) {

        // A second call would interfere with the first one. While the picker is open the iframe
        // covers the whole window, so this only happens on a double click while it is loading.
        if (AssetPicker.isOpenOrLoading) {
            return null
        }

        AssetPicker.isOpenOrLoading = true

        try {
            const frameAndSettings = await AssetPicker.getFrameAndSettings()

            if (!frameAndSettings) {
                return null
            }

            const asset = await AssetPicker.showPicker(frameAndSettings.frame, frameAndSettings.settings)

            if (asset && !AssetPicker.isFileTypeAccepted(asset, accept)) {
                Backbone.View.layout.openStatus(wrongFileTypeMessage, 'error')

                return null
            }

            return asset
        } finally {
            AssetPicker.isOpenOrLoading = false
        }
    }

    /**
     * Loads the iframe and the settings the first time, and reuses them after that.
     *
     * @return {Promise<Object|null>}   { frame, settings }, or null when they could not be loaded
     */
    static async getFrameAndSettings() {

        if (!AssetPicker.loadingFrameAndSettings) {
            AssetPicker.loadingFrameAndSettings = AssetPicker.loadFrameAndSettings()
        }

        try {
            return await AssetPicker.loadingFrameAndSettings
        } catch {
            // Try again the next time, for example after a network problem.
            AssetPicker.loadingFrameAndSettings = null

            Backbone.View.layout.openStatus(
                window.i18n.gettext('Something went wrong'),
                'error'
            )

            return null
        }
    }

    static async loadFrameAndSettings() {

        const [settings] = await Promise.all([
            $.get(AssetPicker.settingsURL),
            import(
                /* rspackChunkName: "asset-picker-overlay" */
                'styling/components/_assetPickerOverlay.scss'
            ),
        ])

        const frame = await AssetPicker.createFrame()

        try {
            await AssetPicker.loadPickerIntoFrame(frame)
        } catch (error) {
            frame.remove()
            throw error
        }

        return { frame, settings }
    }

    /**
     * Adds a hidden, empty iframe to the page.
     *
     * @return {Promise<HTMLIFrameElement>}     the iframe, once its empty page has loaded
     */
    static async createFrame() {

        const frame = document.createElement('iframe')
        frame.className = 'asset-picker-overlay'
        frame.title = window.i18n.gettext('Add WoodWingAsset')

        // An empty page with the same origin as Learnbeat, which is what makes the picker's
        // relative API calls reach our backend with the user's session. Without the doctype
        // the page would run in quirks mode, which changes how the picker is laid out.
        frame.srcdoc = '<!doctype html>'

        const frameIsLoaded = new Promise((resolve) => {
            frame.addEventListener('load', resolve, { once: true })
        })

        document.body.append(frame)

        await frameIsLoaded

        return frame
    }

    /**
     * Adds the fonts, the theme and the picker script to the iframe's empty page.
     *
     * @param {HTMLIFrameElement} frame     the iframe from createFrame()
     * @return {Promise}    resolves when the theme and the script have loaded
     */
    static async loadPickerIntoFrame(frame) {

        // Created with the iframe's own document, so the script runs in the iframe's window.
        const frameDocument = frame.contentDocument

        const fontStylesheet = frameDocument.createElement('link')
        fontStylesheet.rel = 'stylesheet'
        fontStylesheet.href = AssetPicker.fontStylesheetURL

        const themeStylesheet = frameDocument.createElement('link')
        themeStylesheet.rel = 'stylesheet'
        themeStylesheet.href = AssetPicker.frameStylesheetURL

        const script = frameDocument.createElement('script')
        script.src = AssetPicker.frameScriptURL

        // Wait for the theme as well, so the picker never shows up without it. The font may
        // follow later.
        const themeIsLoaded = AssetPicker.waitUntilLoaded(themeStylesheet)
        const scriptIsLoaded = AssetPicker.waitUntilLoaded(script)

        frameDocument.head.append(fontStylesheet, themeStylesheet)
        frameDocument.body.append(script)

        await Promise.all([themeIsLoaded, scriptIsLoaded])
    }

    /**
     * @param {HTMLLinkElement|HTMLScriptElement} element   stylesheet or script that is about to load
     * @return {Promise}    resolves when it has loaded, rejects when it could not be loaded
     */
    static waitUntilLoaded(element) {
        return new Promise((resolve, reject) => {
            element.addEventListener('load', resolve, { once: true })
            element.addEventListener('error', () => {
                reject(new Error(`The asset picker iframe could not load ${element.src || element.href}`))
            }, { once: true })
        })
    }

    /**
     * Shows the iframe over Learnbeat, opens the picker in it and hides it again afterwards.
     *
     * @param {HTMLIFrameElement} frame     the iframe with the picker loaded
     * @param {Object} settings             environment ('PRD' or 'TEST') and basePath, from the backend
     * @return {Promise<Object|null>}       the Asset the user inserted, or null when no asset was inserted
     */
    static async showPicker(frame, settings) {

        const previouslyFocusedElement = document.activeElement

        frame.classList.add('is-open')

        // Move the keyboard focus into the iframe, so Escape and Tab reach the picker.
        frame.focus()

        // Always hide the iframe again, also when the picker throws. Otherwise the invisible
        // iframe would keep covering Learnbeat and block every click.
        try {
            return await frame.contentWindow.openAssetPicker(AssetPicker.getConfig(settings))
        } finally {
            frame.classList.remove('is-open')
            previouslyFocusedElement?.focus()
        }
    }

    /**
     * Builds the config the picker reads each time it opens.
     *
     * @param {Object} settings     environment ('PRD' or 'TEST') and basePath, from the backend
     * @return {Object}             the config, in the shape the picker expects
     */
    static getConfig(settings) {

        // The picker uses fetch() instead of jQuery, so it doesn't get the headers from
        // $.ajaxSetup in main.js. Send the same ones, so the backend treats it like our
        // other requests.
        const headers = {
            'X-Requested-With': 'XMLHttpRequest',
        }

        const xsrfToken = Cookies.get('XSRF-TOKEN')

        if (xsrfToken) {
            headers['X-XSRF-TOKEN'] = xsrfToken
        }

        return {
            proxyPath: AssetPicker.proxyPath,
            headersFromConsumer: headers,
            assetsProvider: {
                provider: 'WOODWING',
                environment: settings.environment,
                basePath: settings.basePath,
            },
        }
    }

    /**
     * The backend needs the extension in the file name to build the CDN path. The baseName of an
     * asset does not always contain it, so add assetType, which WoodWing fills with the extension.
     *
     * @param {Object} asset    the Asset from the picker
     * @return {String}         file name with extension, for example 'volcano.png'
     */
    static getFileName(asset) {

        if (!asset.assetType) {
            return asset.baseName
        }

        const extension = `.${asset.assetType}`

        if (asset.baseName.toLowerCase().endsWith(extension.toLowerCase())) {
            return asset.baseName
        }

        return asset.baseName + extension
    }

    /**
     * Checks whether an asset fits an upload. Follows the rules of the accept attribute of a
     * file input: 'image/*' matches the start of  the MIME type, '.avif' matches the extension,
     * and anything else must be the exact MIME type. Without an accept string, every file type
     * is accepted, like in the file dialog.
     *
     * @param {Object} asset        the Asset from the picker
     * @param {String} accept       accept string, for example UploadModel.imageMIMETypeAcceptString
     * @return {Boolean}            true when the asset's file type is accepted
     */
    static isFileTypeAccepted(asset, accept) {

        if (!accept) {
            return true
        }

        const lowerCaseMimeType = asset.mimeType.toLowerCase()
        const extensionWithDot = `.${Util.getFileExtension(AssetPicker.getFileName(asset))}`

        for (const acceptedType of accept.split(',')) {

            const lowerCaseAcceptedType = acceptedType.trim().toLowerCase()

            if (lowerCaseAcceptedType.startsWith('.')) {
                if (lowerCaseAcceptedType === extensionWithDot) {
                    return true
                }
            } else if (lowerCaseAcceptedType.endsWith('/*')) {
                // 'image/*' becomes 'image/'
                const mimeTypeGroup = lowerCaseAcceptedType.slice(0, -1)

                if (lowerCaseMimeType.startsWith(mimeTypeGroup)) {
                    return true
                }
            } else if (lowerCaseAcceptedType === lowerCaseMimeType) {
                return true
            }
        }

        return false
    }
}
